import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Toaster } from 'sonner';
import './App.css';

import AdminLogin from './pages/AdminLogin';
import CustomerLogin from './pages/CustomerLogin';
import AuthCallback from './pages/AuthCallback';

import AdminLayout from './components/AdminLayout';
import CustomerLayout from './components/CustomerLayout';

import AdminDashboard from './pages/admin/Dashboard';
import Inventory from './pages/admin/Inventory';
import Invoices from './pages/admin/Invoices';
import InvoiceNew from './pages/admin/InvoiceNew';
import InvoiceDetail from './pages/admin/InvoiceDetail';
import Customers from './pages/admin/Customers';
import Orders from './pages/admin/Orders';
import Messages from './pages/admin/Messages';
import Samples from './pages/admin/Samples';

import Portal from './pages/customer/Portal';
import CustomerInvoices from './pages/customer/CustomerInvoices';
import CustomerInvoiceDetail from './pages/customer/CustomerInvoiceDetail';
import Shop from './pages/customer/Shop';
import CustomerOrders from './pages/customer/CustomerOrders';
import CustomerMessages from './pages/customer/CustomerMessages';
import Profile from './pages/customer/Profile';

import { customerApi } from './lib/api';

function RequireAdmin({ children }) {
  const token = localStorage.getItem('adminToken');
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
}

function CustomerRouter() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // Detect session_id in URL fragment and exchange
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (match) {
      customerApi.post('/auth/session', { session_id: match[1] })
        .then((r) => { setMe(r.data); window.history.replaceState(null, '', window.location.pathname); setLoading(false); })
        .catch(() => { setLoading(false); window.location.href = '/customer/login'; });
      return;
    }
    customerApi.get('/auth/me')
      .then((r) => { setMe(r.data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  const refreshMe = () => customerApi.get('/auth/me').then((r) => setMe(r.data)).catch(() => {});

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[#6b7280]">Loading...</div>;
  if (!me) return <Navigate to="/customer/login" replace />;

  return (
    <CustomerLayout me={me}>
      <Routes>
        <Route path="/" element={<Portal me={me} />} />
        <Route path="/invoices" element={<CustomerInvoices />} />
        <Route path="/invoices/:id" element={<CustomerInvoiceDetail me={me} />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/orders" element={<CustomerOrders />} />
        <Route path="/messages" element={<CustomerMessages me={me} />} />
        <Route path="/profile" element={<Profile me={me} refreshMe={refreshMe} />} />
      </Routes>
    </CustomerLayout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      <Routes>
        <Route path="/" element={<Navigate to="/customer/login" replace />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/customer/login" element={<CustomerLogin />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route path="/admin/*" element={
          <RequireAdmin>
            <AdminLayout>
              <Routes>
                <Route path="/" element={<AdminDashboard />} />
                <Route path="/inventory" element={<Inventory />} />
                {/* "Orders" tab shows invoices (user-facing rename 2026-05) */}
                <Route path="/orders" element={<Invoices />} />
                <Route path="/orders/new" element={<InvoiceNew />} />
                <Route path="/orders/:id" element={<InvoiceDetail />} />
                {/* Legacy /invoices URLs still work (bookmarks / emails) */}
                <Route path="/invoices" element={<Navigate to="/admin/orders" replace />} />
                <Route path="/invoices/new" element={<Navigate to="/admin/orders/new" replace />} />
                <Route path="/invoices/:id" element={<InvoiceDetail />} />
                <Route path="/customers" element={<Customers />} />
                {/* "Requests" tab shows quote requests (renamed from Orders) */}
                <Route path="/requests" element={<Orders />} />
                <Route path="/orders-legacy" element={<Navigate to="/admin/requests" replace />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/samples" element={<Samples />} />
              </Routes>
            </AdminLayout>
          </RequireAdmin>
        } />

        <Route path="/portal/*" element={<CustomerRouter />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
